import { useState } from "react";

import type { McpQuotaPatch } from "../../api/mcp";
import type { AppConfig } from "../../config";
import { Button, Input } from "../../f7";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { Card } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

/**
 * Create a tier by writing its first document.
 *
 * `PATCH /admin/quota/{tier}` on a name with no document creates one, and
 * from then on `GET /admin/tiers` accepts it. A typo in that path is a new
 * tier, so the name is typed twice. The route 422s with no field, so the
 * document starts at `tool_default: -1` (unlimited). Caps are set on the
 * tier's own page.
 */
/**
 * The names a new tier may take: what `GET /admin/tiers` lists today. MCP
 * itself takes any path segment, so "PLUS" would become a tier beside "plus"
 * (DA-3677) — the console holds the line MCP does not.
 */
const TIER_NAME = /^[a-z][a-z0-9-]{0,31}$/;

export function McpNewTier({
  config,
  existing,
  onCreated,
}: {
  config: AppConfig;
  /** Every tier that exists, or null while that is not known yet. */
  existing: readonly string[] | null;
  onCreated: () => void;
}) {
  const write = useWrite<McpQuotaPatch>(config);
  const [name, setName] = useState("");
  const [again, setAgain] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<McpQuotaPatch | null>(null);

  const trimmed = name.trim();
  const typed = trimmed !== "" || again.trim() !== "";
  const matches = trimmed !== "" && trimmed === again.trim();
  const wellFormed = TIER_NAME.test(trimmed);
  // The PATCH that creates a tier also UPDATES one: "plus" typed twice would
  // send tool_default: -1 to the live plus tier and make it unlimited.
  const taken = existing?.includes(trimmed) ?? false;
  const creatable = matches && wellFormed && !taken && existing !== null;
  const busy = write.state.kind === "submitting";

  function submit(reason: string) {
    if (!creatable) return;
    void write
      .submit(`/mcp/admin/quota/${encodeURIComponent(trimmed)}`, {
        method: "PATCH",
        body: JSON.stringify({
          tool_default: -1,
          ...(reason ? { reason } : {}),
        }),
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setName("");
        setAgain("");
        onCreated();
      });
    setConfirming(false);
  }

  return (
    <Card
      title="New tier"
      note="A typo in the name creates a tier. Type it twice. It starts unlimited; caps are set on its page."
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          Tier name
          <Input aria-label="New tier name" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Type the name again
          <Input
            aria-label="Confirm tier name"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
        </label>
        {trimmed !== "" && !wellFormed && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Use lower-case letters, digits and hyphens, starting with a letter (32 at most).
            MCP would keep a name like <span className="ref">PLUS</span> as a tier of its own.
          </p>
        )}
        {wellFormed && taken && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            <span className="ref">{trimmed}</span> is already a tier. Creating it again would
            reset its default to unlimited; change it on its own page instead.
          </p>
        )}
        {typed && !matches && (
          <p className="text-xs text-amber-700 dark:text-amber-400">The two names do not match.</p>
        )}
        {typed && existing === null && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Waiting for the list of tiers, so the name can be checked first.
          </p>
        )}
        <Gate need="quota:write">
          <Button disabled={!creatable || busy} onClick={() => setConfirming(true)}>
            Create MCP tier…
          </Button>
        </Gate>
      </div>

      {result && (
        <div className="mt-3">
          <WriteResult title="Tier created" onDismiss={() => setResult(null)}>
            <p className="text-sm">
              <span className="ref">{result.tier ?? trimmed}</span> exists now.{" "}
              {effectFromSeconds(result.effective_within_seconds, "It")}
            </p>
          </WriteResult>
        </div>
      )}
      <WriteFailure state={write.state} onDismiss={write.reset} onReread={onCreated} />

      <ConfirmDialog
        open={confirming}
        title={`Create ${trimmed}`}
        confirmLabel="Create MCP tier"
        requireReason
        reasonHint="why this tier, e.g. DA-9"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect="The name is created when this write lands. Assigning a merchant to it is a separate change."
      >
        <p>
          <span className="ref">{trimmed}</span> starts with an unlimited tool default. Nothing
          moves a merchant onto it until you change their tier.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
